import CompanySettingTypes from '../../constants/companySettings';
import DataTypes from '../../constants/dataTypes';
import { AgentSubmissionNav, PageHeader } from '../../styles/layoutDimensions';
import { createMap } from '../../utils/arrayUtil';
import { getDateString } from '../../utils/dateUtil';
import { getFormattedPhoneNumber } from '../../utils/formatPhoneUtil';

export const sectionNames = {
  requirements: 'requirements',
  upload: 'upload',
  review: 'review',
  submit: 'submit'
};

export const getSubmissionPageContentHeight = () => `calc(100vh - (${PageHeader.height} + ${AgentSubmissionNav.height})`;

export const getMinimumInsuranceRequirements = (itemRequirements, insuredRequirements, policyTypes) => {
  const combinedRequirements = {};
  const policyTypeMap = createMap(policyTypes, 'id');

  const createRequirementMapping = (returnObject, requirement) => {
    const policyTypeId = requirement.insurancePolicyTypeId;
    const policyPropertyTypeId = requirement.insurancePolicyTypePropertyId;
    const policyType = policyTypeMap[policyTypeId];
    const policyTypePropertyMap = createMap(policyType.properties, 'id');
    const policyTypeProperty = policyTypePropertyMap[policyPropertyTypeId];

    const v2RequirementMap = {
      ...requirement,
      propertyDataType: policyTypeProperty.dataType,
      propertyDisplayName: policyTypeProperty.displayName
    };

    if (policyTypeId) {
      if (returnObject[policyTypeId]) {
        const existingProperty = returnObject[policyTypeId].policyProperties[policyPropertyTypeId];

        if (existingProperty) {
          const newValue = requirement.requiredValue;
          const oldValue = existingProperty.requiredValue;

          if (newValue > oldValue) {
            returnObject[policyTypeId].policyProperties[policyPropertyTypeId].requiredValue = newValue;
          }
        } else {
          returnObject[policyTypeId].policyProperties[policyPropertyTypeId] = v2RequirementMap;
        }
      } else {
        returnObject[policyTypeId] = {
          policyTypeId,
          policyTypeDisplayName: policyType.displayName,
          policyProperties: { [policyPropertyTypeId]: v2RequirementMap }
        };
      }
    }
  };

  Object.values(itemRequirements || {}).map(req => createRequirementMapping(combinedRequirements, req));
  Object.values(insuredRequirements || {}).map(req => createRequirementMapping(combinedRequirements, req));
  return combinedRequirements;
};

function sterilizePropertyValue(value) {
  if (typeof value === 'string') {
    return parseInt(value.replace(/[$,]*/g, ''), 10);
  }

  return value;
}

export const getEnhancedPolicyData = (policyType, insuranceCompany, data) => {
  const enhancedFormData = {
    policyType,
    details: {
      ...data.details,
      effective_at: getDateString(data.details?.effective_at),
      expires_at: getDateString(data.details?.expires_at),
      insurance_company_id: insuranceCompany ? insuranceCompany?.companyId : undefined,
      insurance_company_display_name: insuranceCompany ? insuranceCompany?.companyName : undefined
    },
    properties: Object.keys(data)
      .filter(key => key.startsWith('properties') && data[key])
      .map(key => ({
        id: parseInt(key.split('_').pop(), 10),
        value: data[key]
      })),
    suggestedLimits: data?.suggestedLimits ?? []
  };

  enhancedFormData.savedPolicyData = {
    policyTypeId: policyType.id,
    number: data.details?.number,
    subrogationWaived: data.details?.subrogation_waived,
    additionalInsured: data.details?.additional_insured,
    effectiveAt: getDateString(data.details?.effective_at),
    expiresAt: getDateString(data.details?.expires_at),
    insuranceCompanyId: insuranceCompany ? insuranceCompany?.companyId : undefined,
    insuranceCompany,
    propertyValues: enhancedFormData.properties.map(p => ({
      insurancePolicyTypePropertyId: p.id,
      value: p.value
    })),
    suggestedLimits: enhancedFormData.suggestedLimits
  };

  const propertyHash = createMap(policyType.properties, 'id');

  enhancedFormData.properties = enhancedFormData.properties
    .filter(p => !!p.value)
    .map(({ id, value }) => {
      const propertyDataType = propertyHash[id]?.dataType;

      return ({ id, value: propertyDataType === DataTypes.String ? value : sterilizePropertyValue(value) });
    });

  return enhancedFormData;
};

export const getCertHolderAddressInfo = (company, department) => {
  if (department?.deptAddress1) {
    return {
      name: department?.departmentName,
      address1: department?.deptAddress1 || '',
      address2: department?.deptAddress2 || '',
      city: department?.deptCity || '',
      state: department?.deptState || '',
      zip: department?.deptZip || ''
    };
  }

  return {
    name: company?.companyName,
    address1: company?.companyAddress1 || '',
    address2: company?.companyAddress2 || '',
    city: company?.companyCity || '',
    state: company?.companyState || '',
    zip: company?.companyZip || ''
  };
};

export const getContactFromHierarchy = (company, department, job) => {
  const overrideNameSetting = company?.settings?.find(s => s.name === CompanySettingTypes.AgentSubmissionNameDisplay);
  const overrideEmailSetting = company?.settings?.find(s => s.name === CompanySettingTypes.AgentSubmissionEmailDisplay);
  const overridePhoneSetting = company?.settings?.find(s => s.name === CompanySettingTypes.AgentSubmissionPhoneDisplay);

  if (job?.owner) {
    const jobOwner = job?.owner;
    return {
      name: overrideNameSetting?.isEnabled ? overrideNameSetting?.value : `${jobOwner?.userFirstName} ${jobOwner?.userLastName}`,
      email: overrideEmailSetting?.isEnabled ? overrideEmailSetting?.value : jobOwner?.userEmail,
      phone: overridePhoneSetting?.isEnabled ? getFormattedPhoneNumber(overridePhoneSetting?.value) : getFormattedPhoneNumber(jobOwner?.userPhone)
    };
  }

  if (department?.owner) {
    const departmentOwner = department?.owner;
    return {
      name: overrideNameSetting?.isEnabled ? overrideNameSetting?.value : `${departmentOwner?.userFirstName} ${departmentOwner?.userLastName}`,
      email: overrideEmailSetting?.isEnabled ? overrideEmailSetting?.value : departmentOwner?.userEmail,
      phone: overridePhoneSetting?.isEnabled ? getFormattedPhoneNumber(overridePhoneSetting?.value) : getFormattedPhoneNumber(departmentOwner?.userPhone)
    };
  }

  const companyOwner = company?.contact;

  return {
    name: overrideNameSetting?.isEnabled ? overrideNameSetting?.value : `${companyOwner?.userFirstName} ${companyOwner?.userLastName}`,
    email: overrideEmailSetting?.isEnabled ? overrideEmailSetting?.value : companyOwner?.userEmail,
    phone: overridePhoneSetting?.isEnabled ? getFormattedPhoneNumber(overridePhoneSetting?.value) : getFormattedPhoneNumber(companyOwner?.userPhone)
  };
};

const ABBREVIATIONS = {
  liab: 'liability',
  prof: 'professional',
  pol: 'pollution'
};

const SYNONYMS = {
  'professional liability': ['e&o', 'errors and omissions'],
  'cyber liability': ['cyber']
};

function normalize(str) {
  return str ? str.toLowerCase().replace(/\./g, '').trim() : '';
}

function expandSynonyms(str) {
  const normalized = normalize(str);

  // eslint-disable-next-line no-restricted-syntax
  for (const [canonical, synonyms] of Object.entries(SYNONYMS)) {
    if (normalized === canonical) return canonical;

    // eslint-disable-next-line no-restricted-syntax
    for (const synonym of synonyms) {
      if (normalized === synonym) {
        return canonical;
      }
    }
  }

  return normalized
    ?.split(/\s+/)
    .map(w => ABBREVIATIONS[w] || w)
    .join(' ');
}

function levenshtein(a, b) {
  const matrix = Array.from({ length: b.length + 1 }, () => []);

  for (let i = 0; i <= b.length; i++) matrix[i][0] = i;
  for (let j = 0; j <= a.length; j++) matrix[0][j] = j;

  for (let i = 1; i <= b.length; i++) {
    for (let j = 1; j <= a.length; j++) {
      if (b[i - 1] === a[j - 1]) matrix[i][j] = matrix[i - 1][j - 1];
      else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1,
          matrix[i][j - 1] + 1,
          matrix[i - 1][j] + 1
        );
      }
    }
  }

  return matrix[b.length][a.length];
}

function wordSimilarity(a, b) {
  if (b.startsWith(a)) return 1;
  if (b.includes(a)) return 0.7;

  const distance = levenshtein(a, b);
  const maxLen = Math.max(a.length, b.length);

  return 1 - distance / maxLen;
}

function scoreMatch(query, target) {
  const qWords = expandSynonyms(query).split(' ');
  const tWords = expandSynonyms(target).split(' ');

  let score = 0;

  for (const q of qWords) {
    let bestWordScore = 0;

    for (const t of tWords) {
      const similarity = wordSimilarity(q, t);
      bestWordScore = Math.max(bestWordScore, similarity);
    }

    score += bestWordScore;
  }

  return score / qWords.length;
}

export const customFuzzyFind = (query, options, threshold = 0.5) => {
  let best = null;
  let bestScore = 0;

  for (const option of options) {
    const score = scoreMatch(query, option);

    if (score > bestScore) {
      bestScore = score;
      best = option;
    }
  }

  if (bestScore < threshold) return null;

  return {
    match: best,
    score: bestScore
  };
};
